"use client";
import { Button } from "@/components/ui/button";
import { Grid } from "@/components/ui/grid";
import { LottieVisible } from "@/components/ui/lottie-visible";
import { RealtimeCounter } from "@/components/ui/realtime-counter";
import { cn } from "@/util/cn";
import { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim"; // if you are going to use `loadSlim`, install the "@tsparticles/slim" package too.
import dayjs from "dayjs";
import Link from "next/link";
import { type ReactNode, useEffect } from "react";
import { BackgroundPatternCrypto } from "./features/crypto-pattern";
import { DnaPattern } from "./features/dna-pattern";
import { ParticlesPattern } from "./features/particles";

export function FeaturesV2({
    refs,
}: {
    refs: {
        "split-payments": React.RefObject<HTMLDivElement | null>;
        "co-ownership-campaigns": React.RefObject<HTMLDivElement | null>;
        "content-marketplace": React.RefObject<HTMLDivElement | null>;
        "blockchain-payouts": React.RefObject<HTMLDivElement | null>;
    };
}) {
    // this should be run only once per application lifetime
    useEffect(() => {
        void initParticlesEngine(async (engine) => {
            await loadSlim(engine);
        });
    }, []);

    return (
        <div>
            <div>
                <SingleFeature
                    tag="Real-Time Payouts"
                    title="Withdraw Your AdSense by the Second"
                    text="Get instant access to your earnings with our custom Advancing Protocol. Say goodbye to waiting for YouTube's monthly payout on the 21st."
                    icon={
                        <div className="font-bold">
                            <RealtimeCounter
                                initialAmount={400}
                                initialTime={dayjs().startOf("day").toDate().getTime()}
                                speed={0.001}
                                currency="USD"
                            />
                        </div>
                    }
                    card={<ParticlesPattern type="fireworks" />}
                    cta="Learn More"
                    ctaLink="/blog/realtime-payouts"
                    reverse
                />
            </div>
            <div>
                <SingleFeature
                    tag="Yield"
                    title="Earn Interest on Your YouTube Revenue"
                    text="We are proud to be the first in the world to enable our customers to earn interest on the capital generated from YouTube."
                    icon={<LottieVisible className="size-12" src={"/lottie/Main_Reuse.json"} loop />}
                    card={<ParticlesPattern type="sparkle" />}
                    cta="Learn More"
                    ctaLink="/onboard"
                />
            </div>
            <div ref={refs["blockchain-payouts"]}>
                <SingleFeature
                    tag="Payout Alternatives"
                    title="Fastest Cross Border Payments"
                    text="Receive your payments in any (stable)coin or use traditional bank transfers. The choice is yours."
                    icon={<LottieVisible className="size-12" src={"/lottie/Main_security.json"} loop />}
                    card={<DnaPattern />}
                    cta="Learn More"
                    ctaLink="/blog/future-of-youtube-payouts"
                    reverse
                />
            </div>
            <div ref={refs["split-payments"]}>
                <SingleFeature
                    tag="Channel Protection"
                    title="Content ID and Tax Optimization"
                    text="We help you optimize your revenue, improve your taxes and monetize your content from usage across YouTube."
                    icon={<LottieVisible className="size-12" src={"/lottie/Main_Money.json"} loop />}
                    card={<BackgroundPatternCrypto />}
                    cta="Learn More"
                    ctaLink="/onboard"
                />
            </div>
        </div>
    );
}

function SingleFeature({
    tag,
    title,
    text,
    reverse,
    card,
    icon,
    cta,
    ctaLink,
}: {
    tag: string;
    title: string;
    text: string;
    reverse?: boolean;
    card: ReactNode;
    icon: ReactNode;
    cta: string;
    ctaLink: string;
}) {
    return (
        <div className="relative mx-4 py-12 md:py-20">
            <div className="-z-10 absolute inset-0 h-full w-full">
                <Grid />
            </div>
            <div
                className={cn(
                    "flex flex-col items-center justify-between gap-12 rounded-xl border bg-card p-8 md:flex-row md:p-12",
                    {
                        "md:flex-row-reverse": reverse,
                    },
                )}
            >
                <div className="flex max-w-xl flex-col items-start gap-5">
                    <p className="rounded-full border border-border px-4 py-1.5">{tag}</p>
                    <h2 className="font-bold text-4xl">{title}</h2>
                    <p className="text-lg text-muted-foreground">{text}</p>
                    <Button className="mt-2" asChild>
                        <Link href={ctaLink} target={ctaLink.startsWith("http") ? "_blank" : undefined}>
                            {cta}
                        </Link>
                    </Button>
                </div>

                {/* Card on the right */}
                <div className="h-72 w-full grow md:max-w-lg md:shrink md:grow-0">
                    <div className="relative flex h-full flex-col items-start rounded-lg border bg-black">
                        <Cross className="-left-3 -top-3 absolute z-10 h-6 w-6 text-muted-foreground" />
                        <Cross className="-bottom-3 -left-3 absolute z-10 h-6 w-6 text-muted-foreground" />
                        <Cross className="-right-3 -top-3 absolute z-10 h-6 w-6 text-muted-foreground" />
                        <Cross className="-bottom-3 -right-3 absolute z-10 h-6 w-6 text-muted-foreground" />
                        <div className="absolute inset-0">
                            <Grid />
                        </div>

                        <div className="relative flex h-full w-full items-center justify-center bg-transparent">
                            <div className="group/card relative flex h-full w-full items-center justify-center overflow-hidden bg-transparent">
                                {/* Icon */}
                                <div className="pointer-events-none absolute z-30 flex items-center justify-center rounded-full bg-card/20 p-3 text-4xl text-white backdrop-blur-xs">
                                    {icon}
                                </div>
                                {card}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const Cross = ({ className }: { className: string }) => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className={className}
        >
            <title>Cross</title>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
        </svg>
    );
};
